import {
  MODUL_VORBELEGUNG,
  ROLLEN_LABEL,
  STUFEN_LABEL,
  TEAM_EMAIL_DOMAIN,
  type Freigabe,
  type Me,
  type Mitarbeiter,
  type MitarbeiterListe,
  type ModuleKey,
  type ModulFreischaltung,
  type ModulInfo,
  type PortalRole,
  type Stufe,
} from '@portal/contracts';
import { ArrowLeft, Check, Clock, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { HttpError, verwaltungApi } from '../../api.js';
import { navigate, onLinkClick } from '../../router.js';
import './verwaltung.css';

/**
 * Modul „Verwaltung“ (#811, Durchstich zu SCREENS-VERWALTUNG V01–V03): Mitarbeiterliste, Einladen, Modulfreigaben
 * per Klick (Entwurf → speichern), Deaktivieren, Vier-Augen-Anträge. Texte in Sie-Form; der Identitätsdienst wird nie genannt.
 */
// #820: Route /verwaltung (vorher /system/verwaltung, alte Route → 301 im Caddyfile)
export const VERWALTUNG_PFAD = '/verwaltung';
const DATUM = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin' });
const STATUS_LABEL: Record<Mitarbeiter['status'], string> = { aktiv: 'Aktiv', eingeladen: 'Einladung offen', deaktiviert: 'Deaktiviert' };

function fehlerText(err: unknown): string {
  if (err instanceof HttpError) {
    if (err.status === 401) return 'Ihre Sitzung ist abgelaufen. Bitte laden Sie die Seite neu und melden Sie sich erneut an.';
    if (err.text) return err.text;
    if (err.status === 403) return 'Dafür fehlt Ihnen die Berechtigung.';
    if (err.status === 401) return 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';
    if (err.status === 502 || err.status === 503) return 'Die Benutzerverwaltung ist gerade nicht erreichbar. Bitte versuchen Sie es erneut.';
  }
  return 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
}

function datum(iso: string | null): string {
  return iso ? DATUM.format(new Date(iso)) : 'Noch nicht angemeldet';
}

/** Freischaltung mit geänderter Stufe (null = Modul entfernen) */
function mitStufe(alt: ModulFreischaltung, key: ModuleKey, stufe: Stufe | null): ModulFreischaltung {
  const neu = { ...alt };
  if (stufe) neu[key] = stufe;
  else delete neu[key];
  return neu;
}

function freigabeText(f: Freigabe): string {
  const was = f.art === 'rolle' ? `Rolle ${f.rolle ? ROLLEN_LABEL[f.rolle] : ''}` : `Verwaltung: ${f.stufe ? STUFEN_LABEL[f.stufe] : 'Kein Zugriff'}`;
  return `${was} für ${f.mitarbeiterName}`;
}

type Laden = { kind: 'laden' } | { kind: 'fehler'; text: string } | { kind: 'ok'; daten: MitarbeiterListe };

export function Verwaltung({ me, pathname }: { me: Me; pathname: string }) {
  const [state, setState] = useState<Laden>({ kind: 'laden' });
  // Erfolgsmeldung gilt für genau eine Ansicht (Pfad), damit sie nach Navigation nicht stehen bleibt
  const [meldungZu, setMeldungZu] = useState<{ text: string; pfad: string } | null>(null);
  const meldung = meldungZu?.pfad === pathname ? meldungZu.text : null;

  const laden = useCallback(async () => {
    try {
      setState({ kind: 'ok', daten: await verwaltungApi.liste() });
    } catch (err) {
      setState({ kind: 'fehler', text: fehlerText(err) });
    }
  }, []);
  useEffect(() => void laden(), [laden]);

  const detailId = pathname.startsWith(`${VERWALTUNG_PFAD}/mitarbeiter/`) ? decodeURIComponent(pathname.split('/').pop() ?? '') : null;

  if (state.kind === 'laden') {
    return (
      <p className="sh-status" aria-busy="true">
        Mitarbeitende werden geladen …
      </p>
    );
  }
  if (state.kind === 'fehler') {
    return (
      <section className="vw">
        <h1 className="ap-title">Verwaltung</h1>
        <div className="pb-card vw-hinweis vw-hinweis--fehler" role="alert">
          <p>{state.text}</p>
          <button type="button" className="pb-button pb-button--quiet" onClick={() => void laden()}>
            Erneut versuchen
          </button>
        </div>
      </section>
    );
  }
  const daten = state.daten;
  const aktualisiert = (text: string, pfad = window.location.pathname) => {
    setMeldungZu({ text, pfad });
    void laden();
  };
  if (detailId) {
    const m = daten.items.find((x) => x.id === detailId);
    return m ? (
      <Detail key={m.id} m={m} daten={daten} onGespeichert={aktualisiert} meldung={meldung} />
    ) : (
      <section className="vw">
        <BackLink />
        <div className="pb-card vw-hinweis" role="alert">
          Diese Person gibt es im Team-Bereich nicht.
        </div>
      </section>
    );
  }
  return <Liste me={me} daten={daten} meldung={meldung} onAktualisiert={aktualisiert} />;
}

function BackLink() {
  return (
    <a className="vw-back" href={VERWALTUNG_PFAD} onClick={onLinkClick(VERWALTUNG_PFAD)}>
      <ArrowLeft size={15} aria-hidden /> Zur Mitarbeiterliste
    </a>
  );
}

function StatusBadge({ status }: { status: Mitarbeiter['status'] }) {
  return <span className={`vw-badge vw-badge--${status}`}>{STATUS_LABEL[status]}</span>;
}

function ModulChips({ module, alle }: { module: ModulFreischaltung; alle: ModulInfo[] }) {
  const liste = alle.filter((m) => module[m.key]);
  if (!liste.length) return <span className="vw-muted">Keine Module</span>;
  return (
    <ul className="vw-chips" aria-label="Freigegebene Module">
      {liste.map((m) => (
        <li key={m.key} className="vw-chip">
          {m.label} · {STUFEN_LABEL[module[m.key]!]}
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ V01 Liste ------------------------------------------------------------------ */

function Liste({ me, daten, meldung, onAktualisiert }: { me: Me; daten: MitarbeiterListe; meldung: string | null; onAktualisiert(text: string, pfad?: string): void }) {
  const [einladen, setEinladen] = useState(false);
  const [suche, setSuche] = useState('');
  const ausloeser = useRef<HTMLButtonElement>(null);
  const q = suche.trim().toLowerCase();
  const items = q ? daten.items.filter((m) => m.name.toLowerCase().includes(q) || m.email.includes(q)) : daten.items;
  const zuEntscheiden = daten.offeneFreigaben.filter((f) => !f.eigenerAntrag);
  const eigene = daten.offeneFreigaben.filter((f) => f.eigenerAntrag);

  return (
    <section className="vw" aria-labelledby="vw-titel">
      <div className="ap-head">
        <div>
          <p className="vw-crumb">Verwaltung</p>
          <h1 className="ap-title" id="vw-titel">
            Mitarbeitende
          </h1>
          <p className="ap-sub">Zugänge zum Team-Bereich und freigegebene Module verwalten.</p>
        </div>
        <div className="ap-actions">
          <button ref={ausloeser} type="button" className="pb-button" onClick={() => setEinladen(true)}>
            <UserPlus size={15} aria-hidden /> Mitarbeiter einladen
          </button>
        </div>
      </div>

      {meldung && (
        <p className="vw-hinweis vw-hinweis--ok" role="status">
          {meldung}
        </p>
      )}

      {daten.vierAugen.eingeschaltet && (
        <p className="vw-info">
          <Clock size={15} aria-hidden />
          {daten.vierAugen.aktiv
            ? 'Vier-Augen-Prinzip aktiv: Verwaltungsrechte werden erst wirksam, wenn eine zweite verwaltende Person bestätigt.'
            : 'Vier-Augen-Prinzip eingeschaltet, greift aber erst ab zwei Personen mit Verwaltungsrechten.'}
        </p>
      )}

      {(zuEntscheiden.length > 0 || eigene.length > 0) && (
        <div className="pb-card vw-freigaben">
          <h2>Offene Bestätigungen</h2>
          <ul>
            {zuEntscheiden.map((f) => (
              <FreigabeZeile key={f.id} f={f} onErledigt={onAktualisiert} />
            ))}
            {eigene.map((f) => (
              <li key={f.id} className="vw-freigabe">
                <span>
                  {freigabeText(f)} — wartet auf Bestätigung durch eine zweite verwaltende Person (beantragt von Ihnen am {datum(f.erstelltAm)}).
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="vw-suche">
        <label htmlFor="vw-suche">Name oder E-Mail suchen</label>
        <input id="vw-suche" type="search" value={suche} onChange={(e) => setSuche(e.target.value)} autoComplete="off" />
        <span className="vw-muted" role="status" aria-live="polite">
          {items.length} {items.length === 1 ? 'Person' : 'Personen'}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="pb-card vw-leer">
          {q ? 'Keine Mitarbeitenden passen zu Ihrer Suche.' : 'Noch keine weiteren Zugänge vorhanden. Laden Sie die erste Person ein.'}
        </div>
      ) : (
        <div className="pb-card vw-tabelle-wrap">
          <table className="vw-tabelle">
            <caption className="sr-only">Mitarbeitende mit Status, Rolle und freigegebenen Modulen</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">E-Mail</th>
                <th scope="col">Rolle</th>
                <th scope="col">Module</th>
                <th scope="col">Status</th>
                <th scope="col">Letzte Anmeldung</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const pfad = `${VERWALTUNG_PFAD}/mitarbeiter/${encodeURIComponent(m.id)}`;
                return (
                  <tr key={m.id}>
                    <td data-label="Name">
                      <a href={pfad} onClick={onLinkClick(pfad)} className="vw-name">
                        {m.name}
                      </a>
                      {m.istSelbst && <span className="vw-muted"> (Sie)</span>}
                      {m.freigaben.length > 0 && (
                        <span className="vw-wartet">
                          <Clock size={13} aria-hidden /> wartet auf Bestätigung
                        </span>
                      )}
                    </td>
                    <td data-label="E-Mail" className="vw-mail">
                      {m.email}
                    </td>
                    <td data-label="Rolle">{m.rollen.map((r) => ROLLEN_LABEL[r]).join(', ') || '–'}</td>
                    <td data-label="Module">
                      <ModulChips module={m.module} alle={daten.module} />
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={m.status} />
                    </td>
                    <td data-label="Letzte Anmeldung" className="pb-num">
                      {m.status === 'eingeladen' ? 'Einladung offen' : datum(m.letzteAnmeldung)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="vw-muted vw-fuss">Angemeldet als {me.displayName}. Änderungen werden protokolliert.</p>

      {einladen && (
        <EinladenDialog
          daten={daten}
          onClose={(text) => {
            setEinladen(false);
            // erst nach Schließen/Unmount des modalen Dialogs (vorher ist der Auslöser inert)
            setTimeout(() => ausloeser.current?.focus(), 0);
            if (text) onAktualisiert(text);
          }}
        />
      )}
    </section>
  );
}

function FreigabeZeile({ f, onErledigt }: { f: Freigabe; onErledigt(text: string): void }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const entscheiden = async (e: 'bestaetigen' | 'ablehnen') => {
    setBusy(true);
    setFehler(null);
    try {
      await verwaltungApi.entscheiden(f.id, e);
      onErledigt(e === 'bestaetigen' ? `Bestätigt: ${freigabeText(f)}.` : `Abgelehnt: ${freigabeText(f)}.`);
    } catch (err) {
      setFehler(fehlerText(err));
      setBusy(false);
    }
  };
  return (
    <li className="vw-freigabe">
      <span>
        {freigabeText(f)} — beantragt von {f.antragstellerName} am {datum(f.erstelltAm)}, gültig bis {datum(f.gueltigBis)}.
      </span>
      <span className="vw-freigabe__aktionen">
        <button type="button" className="pb-button" disabled={busy} onClick={() => void entscheiden('bestaetigen')}>
          <Check size={15} aria-hidden /> Bestätigen
        </button>
        <button type="button" className="pb-button pb-button--quiet" disabled={busy} onClick={() => void entscheiden('ablehnen')}>
          <X size={15} aria-hidden /> Ablehnen
        </button>
      </span>
      {fehler && (
        <span className="vw-feldfehler" role="alert">
          {fehler}
        </span>
      )}
    </li>
  );
}

/* --------------------------------------------------------------- Dialog-Rahmen --------------------------------------------------------------- */

function Dialog({ titel, onClose, children, breit }: { titel: string; onClose(): void; children: ReactNode; breit?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titelId = useId();
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`vw-dialog${breit ? ' vw-dialog--breit' : ''}`}
      aria-labelledby={titelId}
      aria-modal="true"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="vw-dialog__kopf">
        <h2 id={titelId} tabIndex={-1}>
          {titel}
        </h2>
        <button type="button" className="vw-icon" aria-label="Schließen" onClick={onClose}>
          <X size={18} aria-hidden />
        </button>
      </div>
      {children}
    </dialog>
  );
}

/* ---------------------------------------------------------- Modul-Matrix (Kacheln) ---------------------------------------------------------- */

function ModulMatrix({
  module,
  wert,
  onChange,
  gesperrt,
  hinweis,
}: {
  module: ModulInfo[];
  wert: ModulFreischaltung;
  onChange(key: ModuleKey, stufe: Stufe | null): void;
  /** Modul → lesbarer Grund, warum es nicht änderbar ist */
  gesperrt?: Partial<Record<ModuleKey, string>>;
  hinweis?: Partial<Record<ModuleKey, ReactNode>>;
}) {
  const name = useId();
  return (
    <div className="vw-matrix">
      {module.map((m) => {
        const grund = gesperrt?.[m.key];
        const grundId = `${name}-${m.key}-grund`;
        return (
          <fieldset key={m.key} className="vw-modul" disabled={Boolean(grund)} aria-describedby={grund ? grundId : undefined}>
            <legend>
              {m.label}
              <span className="vw-modul__bereich">Bereich {m.bereich}</span>
            </legend>
            <div className="vw-stufen">
              {[null, ...m.stufen].map((s) => {
                const aktiv = (wert[m.key] ?? null) === s;
                return (
                  <label key={s ?? 'keine'} className={`vw-kachel${aktiv ? ' is-aktiv' : ''}`}>
                    <input type="radio" name={`${name}-${m.key}`} checked={aktiv} onChange={() => onChange(m.key, s)} />
                    <span>{s ? STUFEN_LABEL[s] : 'Kein Zugriff'}</span>
                    {aktiv && <Check size={14} aria-hidden className="vw-kachel__haken" />}
                  </label>
                );
              })}
            </div>
            {grund && (
              <p id={grundId} className="vw-muted vw-modul__grund">
                {grund}
              </p>
            )}
            {hinweis?.[m.key] && <div className="vw-modul__hinweis">{hinweis[m.key]}</div>}
          </fieldset>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- V03 Einladen ------------------------------------------------------------- */

function EinladenDialog({ daten, onClose }: { daten: MitarbeiterListe; onClose(text?: string): void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rolle, setRolle] = useState<PortalRole>('office');
  const [module, setModule] = useState<ModulFreischaltung>({ ...MODUL_VORBELEGUNG.office });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [feld, setFeld] = useState<{ name?: string; email?: string }>({});
  const gfGesperrt = daten.vierAugen.aktiv;

  const pruefen = () => {
    const f: { name?: string; email?: string } = {};
    if (name.trim().length < 2) f.name = 'Bitte geben Sie den vollständigen Namen ein.';
    const e = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/.test(e)) f.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    else if (!e.endsWith(`@${TEAM_EMAIL_DOMAIN}`)) f.email = `Einladungen sind nur an Adressen @${TEAM_EMAIL_DOMAIN} möglich.`;
    setFeld(f);
    // Fokus auf das erste fehlerhafte Feld (SCREENS-VERWALTUNG § 2)
    if (f.name) document.getElementById('vw-ein-name')?.focus();
    else if (f.email) document.getElementById('vw-ein-mail')?.focus();
    return !f.name && !f.email;
  };

  const senden = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy || !pruefen()) return;
    setBusy(true);
    setFehler(null);
    try {
      const r = await verwaltungApi.einladen({ name: name.trim(), email: email.trim().toLowerCase(), rolle, module });
      const versand =
        r.einladung.versand === 'versendet'
          ? `Die Einladung wurde an ${r.mitarbeiter.email} versendet (gültig bis ${datum(r.einladung.gueltigBis)}).`
          : 'Die Einladungs-E-Mail konnte nicht versendet werden.';
      const vier = r.freigaben.length ? ' Die Verwaltungsrechte warten auf Bestätigung durch eine zweite verwaltende Person.' : '';
      onClose(`${r.mitarbeiter.name} wurde angelegt. ${versand}${vier}`);
    } catch (err) {
      setFehler(fehlerText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog titel="Mitarbeiter einladen" onClose={() => !busy && onClose()} breit>
      <form className="vw-form" onSubmit={(e) => void senden(e)} noValidate aria-busy={busy}>
        <div className="vw-feld">
          <label htmlFor="vw-ein-name">Name (Pflichtfeld)</label>
          <input
            id="vw-ein-name"
            autoComplete="name"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            required
            aria-required="true"
            aria-invalid={Boolean(feld.name)}
            aria-describedby={feld.name ? 'vw-ein-name-f' : undefined}
            autoFocus
          />
          {feld.name && (
            <span id="vw-ein-name-f" className="vw-feldfehler">
              {feld.name}
            </span>
          )}
        </div>
        <div className="vw-feld">
          <label htmlFor="vw-ein-mail">E-Mail-Adresse (Pflichtfeld)</label>
          <input
            id="vw-ein-mail"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            placeholder={`vorname.nachname@${TEAM_EMAIL_DOMAIN}`}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-required="true"
            aria-invalid={Boolean(feld.email)}
            aria-describedby={feld.email ? 'vw-ein-mail-f' : 'vw-ein-mail-h'}
          />
          {feld.email ? (
            <span id="vw-ein-mail-f" className="vw-feldfehler">
              {feld.email}
            </span>
          ) : (
            <span id="vw-ein-mail-h" className="vw-muted">
              Die Adresse ist zugleich die Anmeldekennung. Nur @{TEAM_EMAIL_DOMAIN}.
            </span>
          )}
        </div>
        <div className="vw-feld">
          <label htmlFor="vw-ein-rolle">Rolle</label>
          <select
            id="vw-ein-rolle"
            value={rolle}
            onChange={(e) => {
              const r = e.target.value as PortalRole;
              setRolle(r);
              setModule({ ...MODUL_VORBELEGUNG[r] });
            }}
            aria-describedby={gfGesperrt ? 'vw-ein-rolle-h' : undefined}
          >
            {daten.rollen.map((r) => (
              <option key={r.key} value={r.key} disabled={r.key === 'gf' && gfGesperrt}>
                {r.label}
              </option>
            ))}
          </select>
          {gfGesperrt && (
            <span id="vw-ein-rolle-h" className="vw-muted">
              Geschäftsführung kann wegen des Vier-Augen-Prinzips nicht direkt eingeladen werden.
            </span>
          )}
        </div>
        <fieldset className="vw-feld vw-feld--module">
          <legend>Start-Module</legend>
          <p className="vw-muted">Vorbelegt nach Rolle „{ROLLEN_LABEL[rolle]}“ — per Klick anpassbar.</p>
          <ModulMatrix
            module={daten.module}
            wert={module}
            onChange={(k, s) => setModule((alt) => mitStufe(alt, k, s))}
            hinweis={
              daten.vierAugen.aktiv && module.verwaltung === 'verwalten'
                ? { verwaltung: <span className="vw-wartet">Wird erst nach Bestätigung durch eine zweite verwaltende Person wirksam.</span> }
                : undefined
            }
          />
        </fieldset>
        <p className="vw-info">Die Einladung geht per E-Mail an die angegebene Adresse und ist 72 Stunden gültig. Bei der ersten Anmeldung richtet die Person ihr Passwort und die Zwei-Faktor-Anmeldung ein.</p>
        {fehler && (
          <p className="vw-hinweis vw-hinweis--fehler" role="alert">
            {fehler}
          </p>
        )}
        <div className="vw-dialog__fuss">
          <button type="button" className="pb-button pb-button--quiet" onClick={() => onClose()} disabled={busy}>
            Abbrechen
          </button>
          <button type="submit" className="pb-button" disabled={busy}>
            {busy ? 'Wird angelegt …' : 'Anlegen und einladen'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------- V02 Detail ------------------------------------------------------------- */

function Detail({
  m,
  daten,
  onGespeichert,
  meldung,
}: {
  m: Mitarbeiter;
  daten: MitarbeiterListe;
  onGespeichert(text: string, pfad?: string): void;
  meldung: string | null;
}) {
  const [entwurf, setEntwurf] = useState<ModulFreischaltung>({ ...m.module });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [deaktivieren, setDeaktivieren] = useState(false);
  const titel = useRef<HTMLHeadingElement>(null);
  const deaktivierenBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => titel.current?.focus(), []);

  const aenderungen = daten.module.filter((x) => (entwurf[x.key] ?? null) !== (m.module[x.key] ?? null));
  const offenFuer = (k: ModuleKey) => m.freigaben.find((f) => f.art === 'modul' && f.modul === k);
  const nurLesen = m.geschuetzt || m.status === 'deaktiviert';
  const gesperrt: Partial<Record<ModuleKey, string>> = {};
  for (const x of daten.module) {
    if (m.geschuetzt) gesperrt[x.key] = 'Geschützter Zugang — Änderungen sind hier nicht möglich.';
    else if (m.status === 'deaktiviert') gesperrt[x.key] = 'Der Zugang ist deaktiviert.';
    else if (m.istSelbst && x.key === 'verwaltung') gesperrt[x.key] = 'Ihre eigenen Verwaltungsrechte kann nur eine andere berechtigte Person ändern.';
    else if (offenFuer(x.key)) gesperrt[x.key] = 'Für dieses Modul wartet bereits ein Antrag auf Bestätigung.';
  }
  const hinweis: Partial<Record<ModuleKey, ReactNode>> = {};
  for (const f of m.freigaben) {
    if (f.art === 'modul' && f.modul) {
      hinweis[f.modul] = (
        <span className="vw-wartet">
          <Clock size={13} aria-hidden /> Beantragt: {f.stufe ? STUFEN_LABEL[f.stufe] : 'Kein Zugriff'} — wartet auf Bestätigung durch{' '}
          {f.eigenerAntrag ? 'eine zweite verwaltende Person' : 'Sie oder eine weitere verwaltende Person'} (beantragt von {f.antragstellerName},{' '}
          {datum(f.erstelltAm)}).
        </span>
      );
    }
  }
  if (daten.vierAugen.aktiv && !hinweis.verwaltung && !gesperrt.verwaltung && (entwurf.verwaltung ?? null) !== (m.module.verwaltung ?? null)) {
    if (entwurf.verwaltung === 'verwalten' || m.module.verwaltung === 'verwalten') {
      hinweis.verwaltung = <span className="vw-wartet">Vier-Augen-Prinzip: wird erst nach Bestätigung durch eine zweite verwaltende Person wirksam.</span>;
    }
  }

  const speichern = async () => {
    setBusy(true);
    setFehler(null);
    const erledigt: string[] = [];
    const wartet: string[] = [];
    try {
      for (const x of aenderungen) {
        const r = await verwaltungApi.modulSetzen(m.id, { modul: x.key, stufe: entwurf[x.key] ?? null });
        (r.status === 'wartet_freigabe' ? wartet : erledigt).push(x.label);
      }
      const teile = [];
      if (erledigt.length) teile.push(`Änderungen gespeichert (${erledigt.join(', ')}).`);
      if (wartet.length) teile.push(`Zur Bestätigung vorgemerkt: ${wartet.join(', ')} — wartet auf eine zweite verwaltende Person.`);
      // Review #24 R811-1: beantragte Werte sind übermittelt, nicht wirksam → Entwurf zurück auf den Ist-Stand
      setEntwurf((alt) => {
        let neu = alt;
        for (const x of aenderungen) if (wartet.includes(x.label)) neu = mitStufe(neu, x.key, m.module[x.key] ?? null);
        return neu;
      });
      onGespeichert(teile.join(' '));
      setBusy(false);
    } catch (err) {
      setFehler(`${erledigt.length ? `Teilweise gespeichert (${erledigt.join(', ')}). ` : ''}${fehlerText(err)}`);
      setBusy(false);
    }
  };

  return (
    <section className="vw" aria-labelledby="vw-detail-titel">
      <BackLink />
      <div className="ap-head">
        <div>
          <h1 className="ap-title" id="vw-detail-titel" ref={titel} tabIndex={-1}>
            {m.name}
          </h1>
          <p className="ap-sub vw-mail">{m.email}</p>
        </div>
        <StatusBadge status={m.status} />
      </div>
      {meldung && (
        <p className="vw-hinweis vw-hinweis--ok" role="status">
          {meldung}
        </p>
      )}
      <div className="vw-detail">
        <div className="pb-card vw-detail__haupt">
          <h2>Modulfreigaben</h2>
          <p className="vw-muted">Kein Zugriff · Lesen: ansehen · Bearbeiten: Inhalte ändern · Verwalten: zusätzlich Einstellungen und Freigaben.</p>
          <ModulMatrix
            module={daten.module}
            wert={entwurf}
            gesperrt={gesperrt}
            hinweis={hinweis}
            onChange={(k, s) => setEntwurf((alt) => mitStufe(alt, k, s))}
          />
          {!nurLesen && (
            <div className="vw-leiste" aria-live="polite">
              <span>{aenderungen.length ? `${aenderungen.length} ungespeicherte ${aenderungen.length === 1 ? 'Änderung' : 'Änderungen'}` : 'Keine Änderungen'}</span>
              <button type="button" className="pb-button pb-button--quiet" disabled={!aenderungen.length || busy} onClick={() => setEntwurf({ ...m.module })}>
                Verwerfen
              </button>
              <button type="button" className="pb-button" disabled={!aenderungen.length || busy} onClick={() => void speichern()}>
                {busy ? 'Wird gespeichert …' : 'Änderungen speichern'}
              </button>
            </div>
          )}
          {fehler && (
            <p className="vw-hinweis vw-hinweis--fehler" role="alert">
              {fehler}
            </p>
          )}
        </div>
        <div className="pb-card vw-detail__seite">
          <h2>Zugang</h2>
          <dl className="vw-werte">
            <dt>Rolle</dt>
            <dd>{m.rollen.map((r) => ROLLEN_LABEL[r]).join(', ') || '–'}</dd>
            <dt>Status</dt>
            <dd>{STATUS_LABEL[m.status]}</dd>
            <dt>Letzte Anmeldung</dt>
            <dd className="pb-num">{m.status === 'eingeladen' ? 'Einladung noch nicht angenommen' : datum(m.letzteAnmeldung)}</dd>
          </dl>
          {m.geschuetzt ? (
            <p className="vw-muted">Geschützter Zugang: nur Anzeige.</p>
          ) : m.istSelbst ? (
            <p className="vw-muted">Ihren eigenen Zugang kann nur eine andere berechtigte Person deaktivieren.</p>
          ) : m.status !== 'deaktiviert' ? (
            <button ref={deaktivierenBtn} type="button" className="pb-button pb-button--quiet vw-gefahr" onClick={() => setDeaktivieren(true)}>
              Zugang deaktivieren
            </button>
          ) : (
            <p className="vw-muted">Der Zugang ist deaktiviert. Anmeldungen werden abgewiesen.</p>
          )}
        </div>
      </div>
      {deaktivieren && (
        <DeaktivierenDialog
          m={m}
          onClose={(text) => {
            setDeaktivieren(false);
            if (text) {
              onGespeichert(text, VERWALTUNG_PFAD);
              navigate(VERWALTUNG_PFAD);
            } else setTimeout(() => deaktivierenBtn.current?.focus(), 0);
          }}
        />
      )}
    </section>
  );
}

function DeaktivierenDialog({ m, onClose }: { m: Mitarbeiter; onClose(text?: string): void }) {
  const [grund, setGrund] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const los = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await verwaltungApi.deaktivieren(m.id, { grund: grund.trim() || undefined });
      onClose(`Der Zugang von ${m.name} wurde deaktiviert. Bestehende Anmeldungen wurden beendet.`);
    } catch (err) {
      setFehler(fehlerText(err));
      setBusy(false);
    }
  };
  return (
    <Dialog titel="Zugang deaktivieren?" onClose={() => !busy && onClose()}>
      <div className="vw-form">
        <p>
          <strong>{m.name}</strong> ({m.email}) kann sich danach nicht mehr anmelden. Bestehende Anmeldungen werden sofort beendet. Freigaben bleiben
          gespeichert.
        </p>
        <div className="vw-feld">
          <label htmlFor="vw-deak-grund">Grund (optional)</label>
          <input id="vw-deak-grund" value={grund} maxLength={200} onChange={(e) => setGrund(e.target.value)} />
        </div>
        {fehler && (
          <p className="vw-hinweis vw-hinweis--fehler" role="alert">
            {fehler}
          </p>
        )}
        <div className="vw-dialog__fuss">
          <button type="button" className="pb-button pb-button--quiet" onClick={() => onClose()} disabled={busy} autoFocus>
            Abbrechen
          </button>
          <button type="button" className="pb-button vw-gefahr" onClick={() => void los()} disabled={busy}>
            {busy ? 'Wird deaktiviert …' : 'Zugang deaktivieren'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
